import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import { productsAPI } from '../services/api';
import Footer from '../components/Footer.jsx';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await productsAPI.getCategories();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_name === selectedCategory);

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setShowModal(true);
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
    setShowModal(false);
  };

  const nextImage = () => {
    if (selectedProduct && selectedProduct.imageUrls) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProduct.imageUrls.length);
    }
  };

  const prevImage = () => {
    if (selectedProduct && selectedProduct.imageUrls) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProduct.imageUrls.length) % selectedProduct.imageUrls.length);
    }
  };

  const orderViaWhatsApp = (product) => {
    const message = encodeURIComponent(
      `Hi! I would like to order:\n\nProduct: ${product.name}\nPrice: ₹${product.price}\n\nPlease provide more details.`
    );
    window.open(`https://wa.me/918879424647?text=${message}`, '_blank');
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <h1 style={{ textAlign: 'center', color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", fontSize: '36px', marginBottom: '10px' }}>
            Our Shop
          </h1>
          <p style={{ textAlign: 'center', color: 'var(--muted)', marginBottom: '30px' }}>
            Explore our collection of authentic spiritual and astrological items
          </p>

          {/* Category Filter */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'book' : 'ghost'}
              style={{ padding: '8px 16px' }}
            >
              All Products
            </button>
            {categories.map((cat) => (
              <button
                key={cat.category_id}
                onClick={() => setSelectedCategory(cat.category_name)}
                className={selectedCategory === cat.category_name ? 'book' : 'ghost'}
                style={{ padding: '8px 16px' }}
              >
                {cat.category_name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading products...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
              <h3 style={{ color: 'var(--muted)' }}>No products available yet</h3>
              <p style={{ color: 'var(--muted)' }}>Check back soon for our amazing collection!</p>
            </div>
          ) : (
            <div className="grid-3">
              {filteredProducts.map((product) => {
                // Handle both old format (images string) and new format (imageUrls array)
                let imageArray = [];
                if (product.imageUrls && Array.isArray(product.imageUrls)) {
                  imageArray = product.imageUrls;
                } else if (product.images && typeof product.images === 'string') {
                  imageArray = product.images.split(',').map(img => img.trim());
                }
                
                // Filter for valid images - Base64 must be longer than just the prefix
                const validImages = imageArray.filter(img => 
                  img && (
                    img.startsWith('http://') || 
                    img.startsWith('https://') || 
                    (img.startsWith('data:image') && img.length > 50)
                  )
                );
                
                const primaryImage = validImages[0] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

                return (
                  <div 
                    key={product.product_id} 
                    className="card" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => openProductModal(product)}
                  >
                    <div style={{
                      width: '100%',
                      height: '200px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      marginBottom: '12px'
                    }}>
                      <img
                        src={primaryImage}
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { 
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <h3 style={{ margin: '0 0 8px', color: 'var(--maroon)' }}>{product.name}</h3>
                    {product.category_name && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'rgba(122,19,43,0.1)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: 'var(--maroon)',
                        marginBottom: '8px'
                      }}>
                        {product.category_name}
                      </span>
                    )}
                    <p style={{ color: 'var(--muted)', fontSize: '14px', margin: '8px 0' }}>
                      {product.description}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                      <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--maroon)' }}>
                        ₹{product.price}
                      </span>
                      <button
                        className="book"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent modal from opening
                          orderViaWhatsApp(product);
                        }}
                        style={{ padding: '8px 16px' }}
                      >
                        Order via WhatsApp
                      </button>
                    </div>
                    {product.stock_quantity !== undefined && (
                      <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '8px' }}>
                        {product.stock_quantity > 0
                          ? `In Stock (${product.stock_quantity})`
                          : 'Out of Stock'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Product Detail Modal */}
        {showModal && selectedProduct && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={closeProductModal}
          >
            <div 
              style={{
                background: '#fff',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeProductModal}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'var(--maroon)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                ×
              </button>

              <div style={{ position: 'relative' }}>
                {(() => {
                  let imageArray = [];
                  if (selectedProduct.imageUrls && Array.isArray(selectedProduct.imageUrls)) {
                    imageArray = selectedProduct.imageUrls;
                  } else if (selectedProduct.images && typeof selectedProduct.images === 'string') {
                    imageArray = selectedProduct.images.split(',').map(img => img.trim());
                  }
                  
                  const validImages = imageArray.filter(img => 
                    img && (
                      img.startsWith('http://') || 
                      img.startsWith('https://') || 
                      (img.startsWith('data:image') && img.length > 50)
                    )
                  );
                  
                  const currentImage = validImages[currentImageIndex] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="300"%3E%3Crect width="600" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

                  return (
                    <>
                      <img
                        src={currentImage}
                        alt={selectedProduct.name}
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          borderRadius: '16px 16px 0 0'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="300"%3E%3Crect width="600" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {validImages.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            style={{
                              position: 'absolute',
                              left: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              fontSize: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ‹
                          </button>
                          <button
                            onClick={nextImage}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              fontSize: '20px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ›
                          </button>
                          <div style={{
                            position: 'absolute',
                            bottom: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {currentImageIndex + 1} / {validImages.length}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              <div style={{ padding: '30px' }}>
                <h2 style={{
                  margin: '0 0 20px',
                  color: 'var(--maroon)',
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '28px'
                }}>
                  {selectedProduct.name}
                </h2>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px',
                    color: 'var(--muted)',
                    fontSize: '16px'
                  }}>
                    <span>🏷️</span>
                    <strong>Category:</strong>
                    <span>{selectedProduct.category_name || selectedProduct.category}</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: 'var(--muted)',
                    fontSize: '16px'
                  }}>
                    <span>💰</span>
                    <strong>Price:</strong>
                    <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--maroon)' }}>₹{selectedProduct.price}</span>
                  </div>
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{
                    margin: '0 0 15px',
                    color: 'var(--maroon)',
                    fontSize: '20px'
                  }}>
                    Description
                  </h3>
                  <p style={{
                    color: 'var(--muted)',
                    lineHeight: '1.6',
                    fontSize: '16px',
                    margin: 0
                  }}>
                    {selectedProduct.description}
                  </p>
                </div>

                <button
                  className="book"
                  onClick={() => orderViaWhatsApp(selectedProduct)}
                  style={{ width: '100%' }}
                >
                  Order via WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

        {/* <footer style={{ marginTop: '50px' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center', color: 'var(--muted)' }}>
            © {new Date().getFullYear()} Shree Rashi Jyotish.
          </div>
        </footer> */}

        <Footer />

      </main>
    </div>
  );
};

export default Shop;